/**
 * 吃饭睡觉补贴活动 - 时段判断工具
 *
 * startTime / endTime 为后端返回的 "HH:mm:ss" 格式字符串
 * 时间比较统一使用 dayjs
 */

import dayjs from 'dayjs';

/**
 * 将 "HH:mm:ss" 转为今天的 dayjs 对象
 */
function toTodayTime(timeStr) {
  const today = dayjs().format('YYYY-MM-DD');
  return dayjs(`${today} ${timeStr}`);
}

/**
 * 判断当前时间是否落在某子任务时段内
 * @param {Object} subTask { startTime: "HH:mm:ss", endTime: "HH:mm:ss" }
 * @returns {boolean}
 */
export function isInPeriod(subTask) {
  if (!subTask || !subTask.startTime || !subTask.endTime) return false;
  const now = dayjs();
  // 使用 !isBefore 实现 >= startTime，避免恰好等于 startTime 时判断为不在时段内
  return !now.isBefore(toTodayTime(subTask.startTime))
    && now.isBefore(toTodayTime(subTask.endTime));
}

/**
 * 判断某子任务是否已过期（当前时间 > endTime）
 * @param {Object} subTask
 * @returns {boolean}
 */
export function isExpired(subTask) {
  if (!subTask || !subTask.endTime) return false;
  return dayjs().isAfter(toTodayTime(subTask.endTime));
}

/**
 * 根据时间返回当前时段任务和下一个任务
 * 纯时间判断，不涉及业务状态
 * @param {Array} subTasks - 按时间顺序排列的子任务列表
 * @returns {{ current: Object|null, next: Object|null }}
 *   current: 当前正处于时段内的任务（没有则为 null）
 *   next: 下一个未开始的任务；若都过期则循环取第一个（代表明天）
 */
export function getTasksByTime(subTasks) {
  if (!subTasks || subTasks.length === 0) {
    return { current: null, next: null };
  }

  let current = null;
  let next = null;

  for (let i = 0; i < subTasks.length; i += 1) {
    const task = subTasks[i];
    if (isInPeriod(task)) {
      current = task;
      // 下一个就是列表中的下一项，循环到头取第一个
      next = subTasks[i + 1] || subTasks[0];
      break;
    }
    if (!isExpired(task)) {
      // 还没开始的第一个就是 next
      next = task;
      break;
    }
  }

  // 所有都过期了 → next 取第一个（明天循环）
  if (!current && !next) {
    [next] = subTasks;
  }

  return { current, next };
}
